import { divideRounded } from '../shared/money';
import { DomainError } from '../shared/errors';

/**
 * Weighted-average cost pool (ADR-0019): a running quantity (base unit, scale 1000) and a
 * running total value (minor units). No rounded unit cost is ever stored, so the pool value
 * always equals the sum of the posted value deltas.
 */
export interface CostPool {
  readonly qtyScaled: number;
  readonly valueMinor: number;
}

export const EMPTY_POOL: CostPool = { qtyScaled: 0, valueMinor: 0 };

export interface PoolPosting {
  /** Signed change in quantity; 0 only for a revaluation. */
  qtyDeltaScaled: number;
  /**
   * Inbound: the value of the goods (receipt cost). Omitted = valued at the current average.
   * Outbound: must be omitted (always valued at the average).
   * Revaluation (qty 0): the signed value change.
   */
  valueMinor?: number;
}

export interface PoolResult {
  qtyDeltaScaled: number;
  valueDeltaMinor: number;
  after: CostPool;
}

function safe(n: bigint, what: string): number {
  if (n > BigInt(Number.MAX_SAFE_INTEGER) || n < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new DomainError('MONEY_OVERFLOW', `${what} is too large`);
  }
  return Number(n);
}

/** Value of `qtyScaled` at the pool's average cost, rounded half-up. */
export function valueAtAverage(pool: CostPool, qtyScaled: number): number {
  if (pool.qtyScaled <= 0) {
    throw new DomainError('VALIDATION_FAILED', 'The item has no average cost yet', {
      reason: 'no_average_cost',
    });
  }
  return safe(
    divideRounded(BigInt(qtyScaled) * BigInt(pool.valueMinor), BigInt(pool.qtyScaled), 'half_up'),
    'Value',
  );
}

export function postToPool(pool: CostPool, posting: PoolPosting): PoolResult {
  const { qtyDeltaScaled: dq } = posting;
  if (!Number.isSafeInteger(dq)) {
    throw new DomainError('VALIDATION_FAILED', 'Quantity must be a safe integer');
  }
  let dv: number;
  if (dq > 0) {
    if (posting.valueMinor !== undefined) {
      if (!Number.isSafeInteger(posting.valueMinor) || posting.valueMinor < 0) {
        throw new DomainError('VALIDATION_FAILED', 'Inbound value must be a non-negative integer');
      }
      dv = posting.valueMinor;
    } else {
      dv = valueAtAverage(pool, dq);
    }
  } else if (dq < 0) {
    if (posting.valueMinor !== undefined) {
      throw new DomainError('INVARIANT_VIOLATION', 'Outbound postings are valued at average cost');
    }
    const out = -dq;
    if (out > pool.qtyScaled) {
      throw new DomainError('INSUFFICIENT_STOCK', 'Not enough stock to post this movement', {
        available_scaled: pool.qtyScaled,
        requested_scaled: out,
      });
    }
    // Taking everything takes the whole value: no residue is stranded in an empty pool.
    dv = out === pool.qtyScaled ? -pool.valueMinor : -valueAtAverage(pool, out);
  } else {
    if (posting.valueMinor === undefined || !Number.isSafeInteger(posting.valueMinor)) {
      throw new DomainError('VALIDATION_FAILED', 'A revaluation needs a value change');
    }
    if (pool.qtyScaled <= 0) {
      throw new DomainError('VALIDATION_FAILED', 'Cannot revalue an empty pool');
    }
    if (pool.valueMinor + posting.valueMinor < 0) {
      throw new DomainError('VALIDATION_FAILED', 'Revaluation would make the value negative');
    }
    dv = posting.valueMinor;
  }
  const after: CostPool = {
    qtyScaled: safe(BigInt(pool.qtyScaled) + BigInt(dq), 'Quantity'),
    valueMinor: safe(BigInt(pool.valueMinor) + BigInt(dv), 'Value'),
  };
  return { qtyDeltaScaled: dq, valueDeltaMinor: dv === 0 ? 0 : dv, after };
}

/** Rebuilds a pool from its ledger (reconciliation): Σ deltas in posting order. */
export function replayPool(
  deltas: readonly { qtyDeltaScaled: number; valueDeltaMinor: number }[],
): CostPool {
  let qty = 0n;
  let value = 0n;
  for (const d of deltas) {
    qty += BigInt(d.qtyDeltaScaled);
    value += BigInt(d.valueDeltaMinor);
  }
  return { qtyScaled: safe(qty, 'Quantity'), valueMinor: safe(value, 'Value') };
}

/**
 * Average unit cost per *display* unit for reports, e.g. per kg (`perScaled` = 1 000 000)
 * or per piece (`perScaled` = 1000). Rounded half-up; never used for posting.
 */
export function averageUnitCost(pool: CostPool, perScaled: number): number | null {
  if (pool.qtyScaled <= 0) return null;
  return safe(
    divideRounded(BigInt(pool.valueMinor) * BigInt(perScaled), BigInt(pool.qtyScaled), 'half_up'),
    'Unit cost',
  );
}
